import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  LogIn, LogOut, ShieldCheck, Mail, BookMarked,
  Check, X, ExternalLink, Loader2, MailOpen, Radar,
} from "lucide-react";
import { Wordmark } from "./Wordmark";
import { AeoPanel } from "./AeoPanel";
import { useAuth } from "./AuthContext";
import {
  adminLogin, adminMe, adminLogout,
  getAdminStats, getAdminMessages, markMessageRead,
  getAdminReferences, moderateReference,
} from "@/lib/rexxponsApi";

const StatCard = ({ label, value, accent }) => (
  <div className="bg-card border border-border rounded-xl p-5" data-testid="admin-stat">
    <p className="text-xs tracking-[0.16em] uppercase text-muted-foreground mb-2">{label}</p>
    <p className={`font-heading text-3xl font-light ${accent ? "text-primary" : "text-foreground"}`}>{value ?? "—"}</p>
  </div>
);

const LoginScreen = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(email.trim(), password);
      toast.success("Welcome back.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-card border border-border rounded-2xl p-8 shadow-2xl" data-testid="admin-login-form">
        <div className="flex items-center gap-2 mb-1">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 border border-primary/30">
            <ShieldCheck strokeWidth={1.5} className="w-4 h-4 text-primary" />
          </span>
          <Wordmark className="text-xl text-foreground" />
        </div>
        <h1 className="font-heading text-2xl font-medium mt-4 mb-1">Control Panel</h1>
        <p className="text-sm text-muted-foreground mb-6">Sign in to manage submissions and messages.</p>
        <input
          data-testid="admin-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          placeholder="Email"
          className="w-full bg-secondary/60 border border-border rounded-lg px-4 py-2.5 text-sm mb-3 focus:outline-none focus:border-primary/50 transition-colors"
        />
        <input
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
          placeholder="Password"
          className="w-full bg-secondary/60 border border-border rounded-lg px-4 py-2.5 text-sm mb-5 focus:outline-none focus:border-primary/50 transition-colors"
        />
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={busy}
          className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm tracking-[0.08em] uppercase rounded-lg px-4 py-3 hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn strokeWidth={1.8} className="w-4 h-4" />}
          Sign in
        </button>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="w-full text-center text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-foreground mt-4 transition-colors"
        >
          ← Back to Rexxpons
        </button>
      </form>
    </div>
  );
};

const Dashboard = ({ onLogout, googleAdmin }) => {
  const [tab, setTab] = useState("messages");
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [references, setReferences] = useState([]);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [s, m, r] = await Promise.all([getAdminStats(), getAdminMessages(), getAdminReferences()]);
      setStats(s);
      setMessages(m.messages || []);
      setReferences(r.references || []);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  useEffect(() => { refresh(); }, [refresh]);

  const onRead = async (id) => {
    try { await markMessageRead(id); refresh(); } catch { toast.error("Couldn't update the message."); }
  };
  const onModerate = async (id, status) => {
    try { await moderateReference(id, status); toast.success(`Reference ${status}.`); refresh(); }
    catch { toast.error("Couldn't update the reference."); }
  };

  const TabBtn = ({ id, icon: Icon, label, badge }) => (
    <button
      data-testid={`admin-tab-${id}`}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs tracking-[0.12em] uppercase transition-colors ${
        tab === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground border border-border"
      }`}
    >
      <Icon strokeWidth={1.6} className="w-3.5 h-3.5" /> {label}
      {badge > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-primary/20 text-primary text-[10px]">{badge}</span>}
    </button>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-24 pb-24" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 group" data-testid="admin-home-link">
          <span className="relative flex items-center justify-center w-7 h-7 rounded-md border border-primary/40 bg-primary/10 group-hover:bg-primary/20 transition-colors">
            <span className="text-primary font-heading text-sm font-semibold">R</span>
          </span>
          <Wordmark className="text-lg text-foreground" />
          <span className="text-xs tracking-[0.16em] uppercase text-muted-foreground ml-2">Control</span>
        </button>
        <button
          data-testid="admin-logout-btn"
          onClick={onLogout}
          className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-foreground border border-border rounded-full px-3 py-2 transition-colors"
        >
          <LogOut strokeWidth={1.5} className="w-3.5 h-3.5" /> {googleAdmin ? "Back to app" : "Log out"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
        <StatCard label="Messages" value={stats?.messages} />
        <StatCard label="Unread" value={stats?.unread_messages} accent />
        <StatCard label="References" value={stats?.references} />
        <StatCard label="Pending" value={stats?.pending_references} accent />
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <TabBtn id="messages" icon={Mail} label="Messages" badge={stats?.unread_messages} />
        <TabBtn id="references" icon={BookMarked} label="References" badge={stats?.pending_references} />
        <TabBtn id="aeo" icon={Radar} label="AEO Dispatcher" />
      </div>

      {tab === "aeo" && <AeoPanel />}

      {tab === "messages" && (
        <div className="space-y-3" data-testid="admin-messages-list">
          {messages.length === 0 && <p className="text-sm text-muted-foreground py-10 text-center">No messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id} data-testid="admin-message-item" className={`bg-card border rounded-xl p-5 ${m.read ? "border-border" : "border-primary/40"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{m.name || "Anonymous"}{m.email && <span className="text-muted-foreground font-normal"> · {m.email}</span>}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(m.created_at).toLocaleString()}</p>
                </div>
                {!m.read && (
                  <button data-testid="admin-message-read-btn" onClick={() => onRead(m.id)} className="flex items-center gap-1.5 text-xs text-primary hover:opacity-80 shrink-0">
                    <MailOpen className="w-3.5 h-3.5" /> Mark read
                  </button>
                )}
              </div>
              <p className="text-sm text-foreground/90 leading-relaxed mt-3 whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "references" && (
        <div className="space-y-3" data-testid="admin-references-list">
          {references.length === 0 && <p className="text-sm text-muted-foreground py-10 text-center">No submissions yet.</p>}
          {references.map((r) => (
            <div key={r.id} data-testid="admin-reference-item" className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{r.title}</p>
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline break-all">
                    {r.url} <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                  {r.note && <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{r.note}</p>}
                  <p className="text-[11px] text-muted-foreground mt-2">
                    {r.submitter ? `by ${r.submitter} · ` : ""}{r.doc_kind || "general"} · {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`text-[10px] tracking-[0.14em] uppercase rounded-full px-2.5 py-1 shrink-0 ${
                  r.status === "approved" ? "bg-primary/15 text-primary" : r.status === "rejected" ? "bg-destructive/15 text-destructive" : "bg-secondary text-muted-foreground"
                }`}>{r.status}</span>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button data-testid="admin-ref-approve-btn" onClick={() => onModerate(r.id, "approved")} disabled={r.status === "approved"} className="flex items-center gap-1.5 text-xs bg-primary/10 text-primary border border-primary/30 rounded-full px-3 py-1.5 hover:bg-primary/20 transition-colors disabled:opacity-40">
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button data-testid="admin-ref-reject-btn" onClick={() => onModerate(r.id, "rejected")} disabled={r.status === "rejected"} className="flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded-full px-3 py-1.5 hover:text-destructive hover:border-destructive/40 transition-colors disabled:opacity-40">
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminPanel() {
  const { user, loading: authLoading } = useAuth();
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const navigate = useNavigate();
  const googleAdmin = !!user?.is_admin;

  useEffect(() => {
    if (authLoading) return;
    if (googleAdmin) { setAuthed(true); return; }
    adminMe().then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, [authLoading, googleAdmin]);

  const logout = async () => {
    if (googleAdmin) { navigate("/"); return; }
    await adminLogout();
    setAuthed(false);
  };

  return (
    <div className="nexx-ambient min-h-screen bg-background text-foreground">
      <Toaster position="top-center" theme="dark" />
      {authed === null && (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}
      {authed === false && <LoginScreen onDone={() => setAuthed(true)} />}
      {authed === true && <Dashboard onLogout={logout} googleAdmin={googleAdmin} />}
    </div>
  );
}
